// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 16-api-conventions.md
import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { PipelineRunDetail } from '@/components/app-detail/PipelineRunDetail';
import { DataList, type DataListColumn } from '@/components/greenlight/DataList';
import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { downloadTextFile } from '@/lib/download';
import { useSessionCursorStack } from '@/lib/useSessionCursorStack';
import type { PipelineFailureListItem } from '@greenlight/contract';
import { BulkActionToolbar } from './BulkActionToolbar';
import { changeLabel, renderPipelineFailuresMarkdown } from './pipelineFailuresMarkdown';

const PAGE_SIZE = 25;
const CURSOR_PARAM = 'pcursor';

const DELIVERY: Record<
  PipelineFailureListItem['delivery']['status'],
  { variant: StatusBadgeVariant; label: string }
> = {
  not_sent: { variant: 'inactive', label: 'Not sent' },
  pending: { variant: 'pending', label: 'Sending' },
  sent: { variant: 'active', label: 'Sent to Shift' },
  gave_up: { variant: 'failed', label: 'Send failed' },
};

const DELIVERY_REASONS: Readonly<Record<string, string>> = {
  'fleet.not_enrolled': 'waiting for this install to enroll with Shift',
  'fleet.no_installation': 'waiting for this install to finish setting up',
};

/** Why a run is still sending, failed to send, or was never sent; `null` once it is with Shift. */
export function deliveryReason(delivery: PipelineFailureListItem['delivery']): string | null {
  if (delivery.status === 'sent') return null;
  if (delivery.status === 'not_sent') {
    return 'Failed runs are sent a minute after they fail; runs that failed more than a week before this release are not sent.';
  }
  if (delivery.error_code === null) return null;
  return (
    DELIVERY_REASONS[delivery.error_code] ?? `the last attempt failed (${delivery.error_code})`
  );
}

export function PipelineFailuresTab(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const cursor = searchParams.get(CURSOR_PARAM) ?? undefined;
  const trail = useSessionCursorStack('feedback-pipeline-failures');
  const clearTrail = trail.clear;
  React.useEffect(() => {
    if (!cursor) clearTrail();
  }, [cursor, clearTrail]);
  const [selected, setSelected] = React.useState<Map<string, PipelineFailureListItem>>(new Map());
  const [open, setOpen] = React.useState<PipelineFailureListItem | null>(null);

  const key = ['feedback-pipeline-failures', cursor ?? null];
  const query = api.feedback.pipelineFailures.useQuery(
    key,
    { query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) } },
    { queryKey: key },
  );
  const items = query.data?.status === 200 ? query.data.body.items : [];
  const nextCursor = query.data?.status === 200 ? query.data.body.next_cursor : null;

  function toggleRow(item: PipelineFailureListItem): void {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.run_id)) next.delete(item.run_id);
      else next.set(item.run_id, item);
      return next;
    });
  }

  function setCursor(next: string | undefined): void {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next) params.set(CURSOR_PARAM, next);
        else params.delete(CURSOR_PARAM);
        return params;
      },
      { replace: true },
    );
  }

  function goNext(): void {
    if (!nextCursor) return;
    trail.push(cursor ?? '');
    setCursor(nextCursor);
  }

  function goPrev(): void {
    const prev = trail.pop();
    setCursor(prev === '' ? undefined : prev);
  }

  function downloadSelected(): void {
    const chosen = [...selected.values()];
    if (chosen.length === 0) return;
    downloadTextFile('greenlight-pipeline-failures.md', renderPipelineFailuresMarkdown(chosen));
    toast.success(`Downloaded ${chosen.length} failure${chosen.length === 1 ? '' : 's'}.`);
    setSelected(new Map());
  }

  const columns: DataListColumn<PipelineFailureListItem>[] = [
    {
      key: 'app',
      header: 'App',
      primary: true,
      render: (i) => (
        <span className="block min-w-0">
          <span className="block truncate">{i.app_name}</span>
          <span className="block truncate font-mono text-[11px] text-muted-foreground">
            {i.app_slug}
          </span>
        </span>
      ),
    },
    {
      key: 'change',
      header: 'Branch or PR',
      render: (i) => <span className="text-[12px]">{changeLabel(i)}</span>,
    },
    {
      key: 'checks',
      header: 'Failing check',
      render: (i) =>
        i.failed_checks.length === 0 ? (
          <span className="text-[12px] text-muted-foreground">None recorded</span>
        ) : (
          <span className="inline-flex flex-wrap gap-1">
            {i.failed_checks.map((c) => (
              <MonoValue key={c.check_name}>{c.check_name}</MonoValue>
            ))}
          </span>
        ),
    },
    {
      key: 'shift',
      header: 'Shift',
      priority: 'md',
      render: (i) => {
        const reason = deliveryReason(i.delivery);
        return (
          <span title={reason ?? undefined}>
            <StatusBadge variant={DELIVERY[i.delivery.status].variant}>
              {DELIVERY[i.delivery.status].label}
            </StatusBadge>
          </span>
        );
      },
    },
    {
      key: 'when',
      header: 'When',
      priority: 'md',
      render: (i) => (
        <span className="text-[12px] text-muted-foreground">
          {new Date(i.completed_at ?? i.triggered_at).toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <BulkActionToolbar
        selectionCount={selected.size}
        actions={[{ label: 'Download .md', onClick: downloadSelected, primary: true }]}
      />

      {query.isError ? (
        <Alert variant="destructive">
          <AlertDescription>
            Couldn&apos;t load pipeline failures. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : query.isLoading ? (
        <div className="space-y-2" data-testid="pipeline-failures-loading">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <>
          <DataList
            items={items}
            columns={columns}
            rowKey={(i) => i.run_id}
            ariaLabel="Pipeline failures"
            selectedKey={open?.run_id}
            selectedKeys={new Set(selected.keys())}
            onToggleRow={toggleRow}
            selectionLabel={(i) => `Select ${i.app_slug} run ${i.commit_sha.slice(0, 7)}`}
            onRowActivate={setOpen}
            empty={
              <div className="rounded-gl border border-dashed border-border p-8 text-center">
                No failed pipeline runs. Runs that fail a check land here.
              </div>
            }
          />
          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} disabled={!cursor}>
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!nextCursor}>
              Next
            </Button>
          </div>
        </>
      )}

      <GLDrawer
        open={open !== null}
        onOpenChange={(o) => !o && setOpen(null)}
        variant="panel"
        defaultWidth={640}
        minWidth={360}
        storageKey="gl-pipeline-failure-detail-width"
        title={
          <div className="min-w-0">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
              Pipeline failure · {open?.app_slug ?? '…'}
            </span>
            <p className="mt-0.5 truncate text-sm font-semibold text-foreground">
              {open ? changeLabel(open) : ' '}
            </p>
          </div>
        }
        srDescription="Pipeline failure detail"
        contentProps={{ 'data-testid': 'pipeline-failure-detail' }}
      >
        {open && <PipelineRunDetail appId={open.app_id} runId={open.run_id} />}
      </GLDrawer>
    </div>
  );
}
